import { designIntentSignature } from './tool-design-contract.ts';
import type { ToolDesignIntent } from './types/tool-design.ts';
import { APP_OBJECT_KIND_LABELS } from './types/app-objects.ts';
import type {
  ForgeUsageSheet,
  UsageSheetMethod,
  UsageSheetSuccess,
  UsageSheetPromiseMode,
  UsageSheetUseRole,
  UsageSheetValidation,
} from './types/forge-usage-sheet.ts';

/** Change detector: a validation applies to one exact sheet only. */
export const usageSheetSignature = (sheet: ForgeUsageSheet): string => designIntentSignature(sheet);

export const isUsageSheetValidated = (
  sheet: ForgeUsageSheet | null | undefined,
  validation: UsageSheetValidation | null | undefined,
): boolean => Boolean(sheet && validation && validation.signature === usageSheetSignature(sheet));

/**
 * The key a sheet input has in the method's steps (`{{input.<key>}}`) and in
 * the built tool: its label in lower case, without accents, words joined by _.
 */
export const sheetInputKey = (label: string): string => {
  const key = label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return /^[a-z]/.test(key) ? key : `input_${key || 'value'}`;
};

/** The test values of the method, by input key (22/10). */
export const methodTestValues = (sheet: ForgeUsageSheet): Record<string, string> =>
  Object.fromEntries(sheet.inputs.map((input) => [sheetInputKey(input.label), (input.testValue ?? '').trim()]));

/** Two sets of test values are the same once empty values are set aside. */
export const sameTestValues = (a: Record<string, string> | undefined, b: Record<string, string>): boolean =>
  JSON.stringify(Object.entries(a ?? {}).filter(([, value]) => value).sort())
    === JSON.stringify(Object.entries(b).filter(([, value]) => value).sort());

/** The proof stands for this exact method tested with these exact test values. */
export const isMethodProofCurrent = (sheet: ForgeUsageSheet): boolean => {
  const method = sheet.method;
  return Boolean(method?.proof && method.proof.signature === usageSheetMethodSignature(method)
    && sameTestValues(method.proof.values, methodTestValues(sheet)));
};

/** The method as tested and built: its proof and the trials are not part of it. */
export const usageSheetMethodSignature = (method: UsageSheetMethod): string => {
  const { proof: _proof, trials: _trials, alternatives: _alternatives, ...rest } = method;
  return designIntentSignature(rest);
};

/** The proof applies only to the exact method it tested. */
export const isUsageSheetMethodProven = (method: UsageSheetMethod | null | undefined): boolean =>
  Boolean(method?.proof && method.proof.status === 'ok' && method.proof.signature === usageSheetMethodSignature(method));

/**
 * Why the sheet's method does not yet do the job, or null when its test
 * reached the success line: the validation then says « Construire quand même ».
 */
export const methodShortfall = (sheet: ForgeUsageSheet): string | null => {
  const method = sheet.method;
  if (!method || method.steps.length === 0) return null;
  const proof = method.proof;
  if (!proof || !isMethodProofCurrent(sheet)) return 'La méthode n’a pas encore été testée.';
  if (proof.status !== 'ok') return `Le test de la méthode a échoué : ${proof.summary}`;
  const enough = sheet.success?.enough ?? null;
  const inTarget = proof.measure?.inTarget ?? null;
  if (enough !== null && inTarget !== null && inTarget < enough) {
    return `Au test, ${inTarget} élément${inTarget > 1 ? 's' : ''} dans la cible sur ${enough} attendus.`;
  }
  return null;
};

/** The success line in one sentence, as the user reads it. */
export const renderSuccessLine = (success: UsageSheetSuccess): string => {
  const wanted = success.wanted.value !== null
    ? `${success.wanted.value} voulus`
    : success.wanted.input ? `autant que l’entrée « ${success.wanted.input} »` : '';
  return `Réussi dès ${success.enough} : ${success.criteria}${wanted ? ` (${wanted})` : ''}.`;
};

/** Steps that gather a list: the step after them judges or writes from their `items`. */
const LIST_STEP_ACTIONS = new Set(['web.collect_list', 'web.follow_each', 'company.registry_each', 'llm.extract_each']);

const renderMethodBrief = (method: UsageSheetMethod): string => {
  const last = method.steps.length;
  const lastAction = method.steps[last - 1]?.action ?? '';
  const listed = LIST_STEP_ACTIONS.has(lastAction);
  return [
    `Méthode validée — figée : ${method.summary}`,
    `Les ${last} étapes suivantes sont recopiées par le code en tête de l'outil, à l'identique, aux positions 1 à ${last} (même actionType, même config, même inputMapping ; une entrée {{input.<clé>}} est une entrée du tool de même libellé) :\n${JSON.stringify(method.steps.map(({ label, action, config, inputMapping }) => ({ label, actionType: action, config, inputMapping })))}`,
    `Écris-les telles quelles en positions 1 à ${last}, puis construis la suite à partir de la position ${last + 1}. Ne les modifie pas et n'ajoute aucune autre recherche ni lecture de page pour cette liste : la méthode est la seule route.`,
    `Jugement de chaque ligne : ${method.judgement}`,
    listed
      ? `Quand le résultat est un tableau : UNE étape llm.judge_each, en dernière position, avec inputMapping.items = {{steps.${last}.output.items}}, config.groupBy = le champ qui nomme l'élément jugé, config.columns = les colonnes du tableau [{key,label}] dont la première porte le nom de l'élément, config.instructions = le jugement ; la section du résultat lit le chemin \`table\`. Pour un autre résultat (synthèse, document), l'étape suivante lit {{steps.${last}.output.itemsText}}. Ne passe jamais tout le tableau à écrire à un seul appel de modèle.`
      : `L'étape ${last + 1} lit la sortie de l'étape ${last}.`,
  ].join('\n');
};

const TRIGGER_LABELS: Record<ForgeUsageSheet['usage']['trigger'], string> = {
  on_demand: 'à la demande',
  schedule: 'à une cadence',
  event: 'sur un événement',
};

const PROMISE_LABELS: Record<UsageSheetPromiseMode, string> = {
  domino: 'Domino le fait',
  with_you: 'Domino le fait avec l’utilisateur',
  not_yet: 'Pas encore possible',
};

const USE_ROLE_BRIEF: Record<UsageSheetUseRole, string> = {
  search: 'oriente la recherche (déjà écrit dans les routes de la méthode ; à garder tel quel)',
  sort: "trie : les instructions de l'étape qui juge l'appliquent explicitement, avec le motif affiché",
  write: "guide la rédaction : les instructions de l'étape qui rédige l'appliquent",
  not_used: "non utilisé : aucune étape ne l'applique",
};

const renderUsesBrief = (sheet: ForgeUsageSheet): string => (sheet.uses ?? []).length > 0
  ? `Ce que l'outil utilise, et où chaque élément agit :\n${(sheet.uses ?? []).map((use) => `- ${use.label} (${use.from}) — ${USE_ROLE_BRIEF[use.role]}`).join('\n')}`
  : '';

/**
 * The validated sheet as the build brief: the assembly step builds exactly
 * this, keeps its course, and treats any gap as a deviation to surface.
 */
export const renderUsageSheetBrief = (sheet: ForgeUsageSheet): string => [
  `Fiche d'usage validée par l'utilisateur. Construis exactement cet outil ; un écart à cette fiche se signale, il ne se décide pas seul.`,
  `Outil : ${sheet.title}`,
  `Cap — problème résolu : ${sheet.cap.problem}`,
  `Cap — critère de réussite : ${sheet.cap.successCriterion}`,
  sheet.success ? `Réussite mesurée : ${renderSuccessLine(sheet.success)} Sinon : ${sheet.success.shortfall}` : '',
  `Utilisateur : ${sheet.job.who} ; moment : ${sheet.job.moment} ; ensuite : ${sheet.job.nextDecision}`,
  `Usage : ${TRIGGER_LABELS[sheet.usage.trigger]} (${sheet.usage.rhythm}), pour ${sheet.usage.audience === 'team' ? "l'équipe" : "l'utilisateur seul"}`,
  `Entrées :\n${sheet.inputs.map((input) => `- ${input.label} [${input.source}] : ${input.proposal} — ${input.why}`).join('\n') || '- aucune'}`,
  `Résultat (${sheet.result.form}) :\n${sheet.result.outputs.map((output) => `- ${output.label} — ${output.why}`).join('\n')}`,
  `Après le résultat : ${sheet.result.afterwards}`,
  `Sources :\n${sheet.sources.map((source) => `- ${source.label} [${source.kind}] — ${source.why}`).join('\n') || '- aucune'}`,
  renderUsesBrief(sheet),
  `Promesses :\n${sheet.promises.map((promise) => `- ${PROMISE_LABELS[promise.mode]} : ${promise.promise}${promise.detail ? ` — ${promise.detail}` : ''}`).join('\n') || '- aucune'}`,
  sheet.appData ? `Données de l'app — collection « ${sheet.appData.title} » : ${sheet.appData.mode === 'runs' ? 'un objet par lancement, identifié par le lancement' : `les mêmes objets se retrouvent entre les lancements ; chaque objet est ${APP_OBJECT_KIND_LABELS[sheet.appData.object ?? 'other']}. Domino retrouve chaque objet par son nom (première colonne de llm.judge_each) et par les colonnes à rôle de config.columns, recopiées depuis les éléments (règle des rôles)`}.
Statuts, dans l'ordre (le premier s'applique aux nouveaux objets) : ${JSON.stringify(sheet.appData.statuses)}. Les statuts, assignations, notes et commentaires de l'équipe ne sont jamais remplacés par le calcul.` : '',
  sheet.method ? renderMethodBrief(sheet.method) : '',
  (sheet.startingPoints ?? []).length > 0
    ? `Point de départ : l'outil se lance depuis un objet de ${(sheet.startingPoints ?? []).map((point) => `« ${point.appTitle} »`).join(', ')}, formulaire prérempli par ses colonnes. Ses entrées décrivent UN objet, jamais une liste.`
    : '',
  (sheet.documents ?? []).length > 0
    ? `Documents de l'outil, donnés tels quels par Domino aux étapes llm.prompt, llm.extract et llm.judge_each (champ context), chacun avec son rôle :\n${(sheet.documents ?? []).map((document) => `- ${document.title} — ${document.role}`).join('\n')}\nN'ajoute aucune étape knowledge.* pour lire ces documents ; dans les instructions des étapes qui rédigent ou jugent, dis comment s'en servir.`
    : '',
  sheet.doubts.length > 0
    ? `Doutes et décisions :\n${sheet.doubts.map((doubt) => `- ${doubt.doubt} → ${doubt.proposal}`).join('\n')}`
    : '',
].filter(Boolean).join('\n\n');

/** The tool's design intent, taken from the validated sheet rather than
 * regenerated by the planner: the sheet the user validated is the one kept. */
export const usageSheetToDesignIntent = (
  sheet: ForgeUsageSheet,
  planned?: Pick<ToolDesignIntent, 'entities' | 'relationships'>,
): ToolDesignIntent => ({
  goal: sheet.cap.problem,
  value: `${sheet.job.who} — ${sheet.job.nextDecision}`,
  inputs: sheet.inputs.map((input) => input.label),
  entities: planned?.entities ?? [],
  relationships: planned?.relationships ?? [],
  outputs: sheet.result.outputs.map((output) => output.label),
  successCriteria: [sheet.cap.successCriterion],
  scope: `${sheet.result.form} ; ${TRIGGER_LABELS[sheet.usage.trigger]} (${sheet.usage.rhythm})`,
  inputDetails: sheet.inputs.map(({ label, source, proposal, why }) => ({ label, source, proposal, why })),
  outputDetails: sheet.result.outputs.map(({ label, why }) => ({ label, why })),
  choices: [],
  doubts: sheet.doubts,
});
